import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
} from "@mui/material";
import { formatValueWithSign } from "components/ui/formatters";
import { SingleValueCardColor, SingleValueCardType } from "constants/index";
import { useState, useCallback } from "react";
import { SingleValueCardProps } from "helpers/types";

const SingleValueCard = ({
  value,
  label,
  description,
  type,
  color,
  onValueChange,
}: SingleValueCardProps) => {
  const [editingValue, setEditingValue] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);

  const displayColor =
    type === SingleValueCardType.PERCENTAGE
      ? value > 0
        ? SingleValueCardColor.SUCCESS
        : value < 0
          ? SingleValueCardColor.ERROR
          : color
      : color;

  const formattedValue = formatValueWithSign(value, type);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    // Remove formatting for editing (show raw number)
    setEditingValue(value.toString());
  }, [value]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);

    let parsedValue: number;
    if (type === SingleValueCardType.CURRENCY) {
      parsedValue = parseFloat(editingValue.replace(/[$,\s]/g, ""));
    } else {
      parsedValue = parseFloat(editingValue.replace(/[%+\s]/g, ""));
    }

    if (!isNaN(parsedValue)) {
      onValueChange(parsedValue);
    }
    setEditingValue("");
  }, [editingValue, type, onValueChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        handleBlur();
      } else if (event.key === "Escape") {
        setIsEditing(false);
        setEditingValue("");
      }
    },
    [handleBlur],
  );

  return (
    <Card
      sx={{ display: "flex", justifyContent: "left", alignItems: "center" }}
    >
      <CardContent>
        <Typography variant="h5">{label}</Typography>
        <TextField
          value={isEditing ? editingValue : formattedValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={(e) => setEditingValue(e.target.value)}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "1.5rem",
              fontWeight: 500,
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              lineHeight: 1.2,
              color: (theme) => theme.palette[displayColor].main,
              "& input": {
                textAlign: "left",
                padding: 0,
              },
            },
          }}
          sx={{
            "& .MuiInputBase-root": {
              fontSize: "1.5rem",
            },
          }}
        />
        <Typography variant="body1">{description}</Typography>
      </CardContent>
    </Card>
  );
};

export default SingleValueCard;
